/* eslint-disable max-len */
import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import { RootState } from '../../redux/rootState';
import DataTable from '../../Common/DataTable';
import ComponentDeleteModal from '../../Common/DeleteModal/ComponentDeleteModal';
import { setLoader } from '../../redux/loaderSlice';
import ComponentPageHeader from '../../Common/PageHeader/ComponentPageHeader';
import { exportToExcelData } from '../../Common/PageHeader/helper';
import { deleteSiteStart, getSitesStart } from './redux/sliceSites';
import {
  resetPagination, setPageNumber, setPageSize, setSearchParam,
  setSortColumn,
  setSortDirection,
} from '../taiReportingSystem/redux/sliceReportWorkspace';

interface SiteData {
  sn: number;
  siteID: string,
  siteName: string,
  address: string,
  country: string,
  city: string,
  state: string,
  pincode: string,
  id: string,
}

export default function Sites() {
  // const [page, setPage] = useState(1);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [initialRender, setInitialRender] = useState(true);
  const {
    pageNumber, pageSize, searchParam, sortColumn, sortDirection,
  } = useSelector((state: RootState) => state.reportWorkspaceReducer);
  const { historyStack } = useSelector((state:RootState) => state.masterDataReducer);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { siteList, isLoading, totalEntries } = useSelector((state: RootState) => state.siteReducer);
  const { showDeleteIcon } = useSelector((state: RootState) => state.masterDataReducer);

  const handleData = () => {
    const params = {
      page: pageNumber,
      rowsPerPage: pageSize,
      searchQuery: searchParam,
      sortCol: sortColumn,
      sortDir: sortDirection,
      showAll,
    };
    dispatch(getSitesStart(params));
  };

  useEffect(() => {
    if (!initialRender) {
      if (pageNumber > 1) {
        dispatch(setPageNumber(1));
      } else {
        handleData();
      }
    } else {
      setInitialRender(false);
    }
  }, [showAll]);

  useEffect(() => {
    handleData();
  }, [pageNumber, pageSize, searchParam, sortColumn, sortDirection]);

  const handleDelete = (id: string) => {
    setShowDeleteModal(true);
    setSelectedSiteId(id);
  };

  const handelDeleteModal = () => {
    const paramData = {
      siteId: selectedSiteId,
      page: pageNumber,
      rowsPerPage: pageSize,
      searchQuery: searchParam,
      sortCol: sortColumn,
      sortDir: sortDirection,
      showAll,
    };
    dispatch(deleteSiteStart(paramData));
    setShowDeleteModal(false);
  };

  const columns = useMemo(
    () => [
      // { key: 'sn', label: 'SN' },
      { key: 'action', label: 'Action' },
      { key: 'siteID', label: 'Site ID' },
      { key: 'siteName', label: 'Site' },
      { key: 'address', label: 'Address' },
      { key: 'country', label: 'Country' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'pincode', label: 'Zip' },
    ],
    [],
  );

  const data: SiteData[] = useMemo(() => siteList.map((item, index) => {
    const transformedDataItem = {
      sn: index,
      action: showDeleteIcon ? 'edit&delete' : 'edit',
      siteID: item?.SiteId,
      siteName: item?.SiteName,
      address: item?.Address,
      country: item?.Country,
      city: item?.City,
      state: item?.State,
      pincode: item?.Pincode,
      id: item?.SiteId,
    };
    return transformedDataItem;
  }), [siteList]);

  useEffect(() => {
    dispatch(setLoader(isLoading));
  }, [isLoading]);

  const handleEdit = (id: string) => {
    navigate(`${id}`);
  };

  const excelData = useMemo(() => siteList.map((item) => {
    const transformedDataItems = {
      Site: item?.SiteName,
      SiteId: item?.SiteId,
      Address: item?.Address,
      Country: item?.Country,
      City: item?.City,
      State: item?.State,
      Zip: item?.Pincode,
    };
    return transformedDataItems;
  }), [siteList]);

  const downloadExcel = () => {
    exportToExcelData(
      {
        data: excelData,
      },
      'Sites_list',
    );
  };

  useEffect(() => {
    if (historyStack) {
      const finderString = 'sites/Site';
      const last = historyStack[historyStack.length - 1];
      const sLast = historyStack[historyStack.length - 2];
      if (!(last?.includes(finderString) || (sLast?.includes(finderString) && last?.includes('/sites')))) {
        dispatch(resetPagination());
      }
    }
  }, [historyStack]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <ComponentPageHeader siteSwitch isChecked={showAll} handleSwitchChange={() => setShowAll(!showAll)} subHeading="Sites" downloadExcel={downloadExcel} />
        </Grid>
        <Grid item xs={12} sm={12} md={12} className="dataTableCustomMargin">
          <DataTable<SiteData>
            data={data}
            columns={columns}
            totalRecords={totalEntries}
            page={pageNumber}
            setPage={(e) => { dispatch(setPageNumber(e)); }}
            rowsPerPage={pageSize}
            setRowsPerPage={(e) => { dispatch(setPageSize(e)); }}
            searchQuery={searchParam}
            setSearchQuery={(e) => { dispatch(setSearchParam(e)); dispatch(setPageNumber(1)); }}
            sortCol={sortColumn}
            setSortCol={(e) => { dispatch(setSortColumn(e)); dispatch(setPageNumber(1)); }}
            sortDir={sortDirection}
            setSortDir={(e) => { dispatch(setSortDirection(e)); dispatch(setPageNumber(1)); }}
            handleData={handleData}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            handleDataFromComponent
          />
        </Grid>
      </Grid>
      <ComponentDeleteModal
        show={showDeleteModal}
        setOpenFrom={() => setShowDeleteModal(false)}
        handleDelete={handelDeleteModal}
      />

    </>
  );
}
